import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTicketAlt, FaPlus, FaEye, FaFilter, FaComments, FaTimes, FaClock, FaUser, FaCalendarAlt, FaFlag, FaTag } from 'react-icons/fa';
import SupportTicketModal from './SupportTicketModal';
import TicketDetailsModal from './TicketDetailsModal';

const SupportTicketsView = ({ userInfo, API_BASE_URL, auth }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: ''
  });
  const [stats, setStats] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalTickets, setTotalTickets] = useState(0);

  const statusColors = {
    open: '#3498db',
    in_progress: '#f39c12',
    resolved: '#27ae60',
    closed: '#95a5a6',
    cancelled: '#e74c3c'
  };

  const priorityColors = {
    low: '#95a5a6',
    medium: '#3498db',
    high: '#f39c12',
    urgent: '#e74c3c'
  };

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [filters, currentPage, itemsPerPage]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [filters]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        created_by: userInfo.user_id,
        page: currentPage + 1, // Backend expects 1-based page numbers
        limit: itemsPerPage,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const response = await axios.get(
        `${API_BASE_URL}/support-tickets?${params}`,
        auth
      );

      // Handle paginated response
      if (response.data.tickets) {
        setTickets(response.data.tickets);
        setTotalTickets(response.data.total || 0);
      } else {
        // Fallback for non-paginated response
        setTickets(response.data);
        setTotalTickets(response.data.length);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setTickets([]);
      setTotalTickets(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/support-tickets/stats?created_by=${userInfo.user_id}`,
        auth
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      category: ''
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => (
    <span style={{
      backgroundColor: statusColors[status] || '#95a5a6',
      color: 'white',
      padding: '0.25rem 0.5rem',
      borderRadius: '0.25rem',
      fontSize: '0.8rem',
      fontWeight: 'bold',
      textTransform: 'uppercase'
    }}>
      {status.replace('_', ' ')}
    </span>
  );

  const getPriorityBadge = (priority) => (
    <span style={{
      backgroundColor: priorityColors[priority] || '#95a5a6',
      color: 'white',
      padding: '0.25rem 0.5rem',
      borderRadius: '0.25rem',
      fontSize: '0.8rem',
      fontWeight: 'bold',
      textTransform: 'uppercase'
    }}>
      {priority}
    </span>
  );

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div>Loading support tickets...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h1 style={{ margin: 0, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FaTicketAlt />
            My Support Tickets
          </h1>
          <p style={{ margin: '0.5rem 0 0 0', color: '#7f8c8d' }}>
            Create and track support requests to your MMDA
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            backgroundColor: '#27ae60',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.25rem',
            cursor: 'pointer',
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <FaPlus />
          Create Ticket
        </button>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#3498db' }}>Total Tickets</h3>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold', color: '#2c3e50' }}>
            {stats.total_tickets || 0}
          </p>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#f39c12' }}>Open</h3>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold', color: '#2c3e50' }}>
            {stats.open_tickets || 0}
          </p>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#27ae60' }}>Resolved</h3>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold', color: '#2c3e50' }}>
            {stats.resolved_tickets || 0}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          <FaFilter />
          <h3 style={{ margin: 0 }}>Filters</h3>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          alignItems: 'end'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Status
            </label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '0.25rem'
              }}
            >
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Priority
            </label>
            <select
              name="priority"
              value={filters.priority}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '0.25rem'
              }}
            >
              <option value="">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div>
            <button
              onClick={clearFilters}
              style={{
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '0.25rem',
                cursor: 'pointer'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Tickets Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {tickets.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#7f8c8d' }}>
            <FaTicketAlt size={48} style={{ marginBottom: '1rem' }} />
            <h3>No tickets found</h3>
            <p>You haven't created any support tickets yet.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                marginTop: '1rem'
              }}
            >
              Create Your First Ticket
            </button>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                  Ticket #
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                  Title
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                  Status
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                  Priority
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                  Created
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => (
                <tr key={ticket.ticket_id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '1rem' }}>
                    <strong>{ticket.ticket_number}</strong>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div>
                      <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                        {ticket.title}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                        {ticket.category.replace('_', ' ')}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {getStatusBadge(ticket.status)}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {getPriorityBadge(ticket.priority)}
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.9rem', color: '#7f8c8d' }}>
                    {formatDate(ticket.created_at)}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button
                      onClick={() => setSelectedTicket(ticket)}
                      style={{
                        backgroundColor: '#3498db',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem',
                        borderRadius: '0.25rem',
                        cursor: 'pointer',
                        marginRight: '0.5rem'
                      }}
                      title="View Details"
                    >
                      <FaEye />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Controls */}
      {totalTickets > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ color: '#7f8c8d' }}>
              Showing {Math.min(currentPage * itemsPerPage + 1, totalTickets)} to {Math.min((currentPage + 1) * itemsPerPage, totalTickets)} of {totalTickets} tickets
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ color: '#7f8c8d' }}>Items per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(0);
                }}
                style={{
                  padding: '0.25rem',
                  borderRadius: '0.25rem',
                  border: '1px solid #ddd'
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          {Math.ceil(totalTickets / itemsPerPage) > 1 && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: currentPage === 0 ? '#ccc' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: currentPage === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                ← Previous
              </button>
              <span style={{ padding: '0.5rem 1rem' }}>
                Page {currentPage + 1} of {Math.ceil(totalTickets / itemsPerPage)}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(Math.ceil(totalTickets / itemsPerPage) - 1, currentPage + 1))}
                disabled={currentPage >= Math.ceil(totalTickets / itemsPerPage) - 1}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: currentPage >= Math.ceil(totalTickets / itemsPerPage) - 1 ? '#ccc' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: currentPage >= Math.ceil(totalTickets / itemsPerPage) - 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create Ticket Modal */}
      <SupportTicketModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        userInfo={userInfo}
        API_BASE_URL={API_BASE_URL}
        auth={auth}
        onSuccess={() => {
          fetchTickets();
          fetchStats();
        }}
      />

      {/* Ticket Details Modal */}
      <TicketDetailsModal
        ticket={selectedTicket}
        onClose={() => setSelectedTicket(null)}
        userInfo={userInfo}
        API_BASE_URL={API_BASE_URL}
        auth={auth}
        onUpdate={() => {
          fetchTickets();
          fetchStats();
        }}
      />

      {/* CSS Styles */}
      <style jsx>{`
        .pagination {
          display: flex;
          list-style: none;
          padding: 0;
          margin: 0;
          gap: 0.25rem;
        }
        .page-item {
          display: flex;
        }
        .page-link {
          padding: 0.5rem 0.75rem;
          text-decoration: none;
          color: #007bff;
          background-color: white;
          border: 1px solid #dee2e6;
          border-radius: 0.25rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        .page-link:hover {
          background-color: #e9ecef;
          border-color: #adb5bd;
        }
        .active .page-link {
          background-color: #007bff;
          color: white;
          border-color: #007bff;
        }
        .disabled .page-link {
          color: #6c757d;
          background-color: white;
          border-color: #dee2e6;
          cursor: not-allowed;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default SupportTicketsView;
